import type {
  OrderProductStockItemInterface,
  PreviewStockItem,
  SegmentInterface,
  StockedProduct,
  StockItemType,
  StoreShortType,
} from '@/types'

/**
 * Получить stock item из продукта
 * Универсальная функция для Cart и Order
 */
function getStockItem(
  record: StockedProduct
):
  | StockItemType
  | OrderProductStockItemInterface
  | PreviewStockItem
  | undefined {
  const supplierStock = record.suppliersStock?.[0]
  if (!supplierStock) return undefined
  return supplierStock.stock?.[0]
}

/**
 * Получить значение из stock item товара (универсальная функция для preview и order данных)
 */
export function getStockItemValue<K extends keyof StockItemType>(
  record: StockedProduct,
  prop: K
): StockItemType[K] | undefined
export function getStockItemValue<T>(
  record: StockedProduct,
  prop: string
): T | undefined
export function getStockItemValue(
  record: StockedProduct,
  prop: string
): unknown {
  const stockItem = getStockItem(record)
  if (!stockItem) return undefined
  return stockItem[prop as keyof typeof stockItem]
}

/**
 * Получить количество товара
 */
export function getProductQuantity(record: StockedProduct): number {
  return getStockItemValue<number>(record, 'quantity') ?? 0
}

/**
 * Получить цену с НДС за единицу товара
 * Для Order: использует total / quantity (total уже включает НДС)
 * Для Cart: возвращает 0 (в Cart нет поля total)
 */
export function getProductPriceWithVAT(record: StockedProduct): number {
  const total = getStockItemValue<number>(record, 'total')
  const quantity = getProductQuantity(record)

  if (total && quantity && quantity > 0) {
    return total / quantity
  }
  return 0
}

export function getSegmentPrice(record: StockedProduct): number {
  return getStockItemValue<number>(record, 'price') ?? 0
}

export function getProductTotalWithVAT(record: StockedProduct): number {
  return getStockItemValue<number>(record, 'total') ?? 0
}

export function getProductRetailPrice(record: StockedProduct): number {
  return getStockItemValue<number>(record, 'retailPrice') ?? 0
}

export function getProductTotalRetailPrice(record: StockedProduct): number {
  return getStockItemValue<number>(record, 'totalRetailPrice') ?? 0
}

/**
 * Форматировать дату доставки
 * Применимо только для Order (в Cart нет поля deliveryDate)
 */
export function formatDeliveryDate(record: StockedProduct): string {
  const deliveryDate = getStockItemValue<string | number>(
    record,
    'deliveryDate'
  )

  if (deliveryDate) {
    const date = new Date(deliveryDate)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('ru-RU')
    }
  }

  return '-'
}

/**
 * Получить store из stock item
 */
export function getStoreFromProduct(
  record: StockedProduct
): StoreShortType | undefined {
  return getStockItemValue<StoreShortType>(record, 'storeShortDto')
}

/**
 * Получить supplier ID из продукта
 */
export function getSupplierIdFromProduct(
  record: StockedProduct
): string | undefined {
  return record.suppliersStock?.[0]?.supplierId
}

/**
 * Получить статус товара в заказе
 * Применимо только для Order (в Cart нет поля status)
 */
export function getProductStatus(record: StockedProduct): string | undefined {
  return getStockItemValue<string>(record, 'status')
}

/**
 * Получить базовую цену (без НДС)
 * Применимо только для Order (в Cart нет поля basicPrice)
 */
export function getBasicPrice(record: StockedProduct): number {
  return getStockItemValue<number>(record, 'basicPrice') ?? 0
}

/**
 * Получить сегмент
 * Применимо только для Order (в Cart нет поля segmentDto)
 */
export function getSegment(
  record: StockedProduct
): SegmentInterface | undefined {
  return getStockItemValue<SegmentInterface>(record, 'segmentDto')
}
